import React, { useEffect, useState } from 'react';
import { useReviewsTools } from '@app/site/secured/review-manager/provider/useReviewsTools';
import useSecurity from '@util/SecurityHook';
import FormButton from '@app/component/form/button/FormButton';
import _isEmpty from 'lodash/isEmpty';
import ReviewListItemFragment from '@app/site/secured/review-manager/fragment/reviews-inbox/ReviewListItemFragment';
import ReviewHandleMessageFragment
	from '@app/site/secured/review-manager/fragment/reviews-inbox/ReviewHandleMessageFragment';
import Message from '@app/component/message/Message';
import ReviewsSorterType from '@app/model/review/input/ReviewsSorterType';
import ReviewListTextFilter from '@app/site/secured/review-manager/fragment/reviews-inbox/ReviewListTextFilter';
import ReviewOutput from '@app/model/review/output/ReviewOutput';

const ReviewsInboxFragment = React.memo(() => {

	const security = useSecurity();
	const tools = useReviewsTools();
	const [reviews, setReviews] = useState<ReviewOutput[]>(tools.reviewOutputs);

	useEffect(() => {
		setReviews(tools.reviewOutputs);
	}, [tools.reviewOutputs]);

	const hasDepartment = !!security.principal!.departmentId;

	const onTextFilter = (text: string) => {
		if (text === '') {
			setReviews(tools.reviewOutputs);
			return;
		}
		setReviews(tools.reviewOutputs.filter(review => review.hasText(text)));
	}

	let assignedToMeCount = 0;
	if (hasDepartment) {
		for (const review of tools.reviewOutputs) {
			if (review.isAssignedTo(security.principal!.departmentId)) {
				assignedToMeCount++;
			}
		}
	}

	const handleAssignedToMe = () => {
		tools.setFilter({ ...tools.filter, isAssignedToMe: !tools.filter.isAssignedToMe });
	};

	const selectedReviewOutput = tools.getSelectedReviewOutput();

	if (_isEmpty(tools.reviewOutputs)) {
		return <div className="min-w-0 w-full lg:flex">
			<Message state="info" extraClassName="w-full">There are no reviews found!</Message>
		</div>;
	}

	return <>
		<div className="md:flex items-center justify-between pb-2.5 lg:flex-none">
			<div className="flex flex-wrap gap-x-3 gap-y-2 items-center">
				<div>
					<select className="input text-xs h-8" value={tools.filter.sorter}
									onChange={(e) => tools.setFilter({ ...tools.filter, sorter: e.target.value as ReviewsSorterType })}>
						<option value={ReviewsSorterType.DATE_DESC}>Most recent</option>
						<option value={ReviewsSorterType.RATING_ASC}>Rating (Lower first)</option>
						<option value={ReviewsSorterType.RATING_DESC}>Rating (Higher first)</option>
					</select>
				</div>
				<FormButton state={tools.filter.isAssignedToMe ? 'primary' : 'white'} extraClassName="text-xs h-7"
										disabled={assignedToMeCount === 0} onClick={handleAssignedToMe}>
					Assigned to me <span className="ml-1 italic text-gray-400">({assignedToMeCount})</span>
				</FormButton>
			</div>
		</div>
		<div className="min-w-0 flex-1 border-t border-gray-200 lg:flex mt-2">
			<aside className="mb-6 shadow lg:shadow-none lg:mb-0 lg:flex-shrink-0">
				<div className="relative flex w-full flex-col border-l border-r border-gray-200 bg-gray-100 lg:w-96">
					<div className="flex-shrink-0">
						<div className="flex h-16 flex-col justify-center bg-white px-6">
							<div className="flex items-baseline space-x-3">
								<h2 className="text-lg font-medium text-gray-900">Inbox</h2>
								<p className="text-sm font-medium text-gray-500">{reviews.length} messages</p></div>
						</div>
						<ReviewListTextFilter onTextFilter={onTextFilter}/>
						<nav aria-label="Message list" className="flex-1 min-h-0 max-h-[250px] lg:max-h-screen overflow-y-auto ">
							<ul role="list" className="divide-y divide-gray-200 border-b border-gray-200">
								{reviews.map((reviewOutput, index) => <ReviewListItemFragment
									key={reviewOutput.review.id}
									reviewOutput={reviewOutput}
									index={index} />)}
							</ul>
						</nav>
					</div>
				</div>
			</aside>
			{!!selectedReviewOutput && <ReviewHandleMessageFragment reviewOutput={selectedReviewOutput} />}
		</div>
	</>;
});

export default ReviewsInboxFragment;
